import { useState, useEffect } from 'react';
import { X, Plus, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UpsellModal({ isOpen, onClose, onProceed, cartProductIds = [] }) {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchUpsellProducts();
    }
  }, [isOpen, cartProductIds]);

  const fetchUpsellProducts = async () => {
    setLoading(true);
    try {
      const ids = cartProductIds.join(',');
      const response = await fetch(`${API}/api/checkout/upsell?cart_product_ids=${ids}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching upsell products:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.product_id === product.product_id);
      if (exists) {
        return prev.filter(p => p.product_id !== product.product_id);
      }
      return [...prev, product];
    });
  };

  const handleAddAndProceed = () => {
    // Add selected products to cart
    selectedProducts.forEach(product => {
      addItem({
        product_id: product.product_id,
        name: product.name,
        price: product.price,
        image_url: product.image_url
      });
    });
    
    if (selectedProducts.length > 0) {
      toast.success(`${selectedProducts.length} producto(s) agregado(s)`);
    }
    
    onProceed();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <ShoppingBag className="w-10 h-10 mb-3" />
          <h2 className="text-2xl font-bold">¡Antes de pagar!</h2>
          <p className="text-emerald-100 mt-1">¿Deseas agregar alguno de estos productos?</p>
        </div>

        {/* Products */}
        <div className="p-6 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : products.length === 0 ? (
            <p className="text-center text-stone-500 py-8">No hay productos sugeridos</p>
          ) : (
            <div className="space-y-3">
              {products.map(product => {
                const isSelected = selectedProducts.find(p => p.product_id === product.product_id);
                const finalPrice = product.promotion_active && product.discount_price 
                  ? product.discount_price 
                  : product.promotion_active && product.discount_percent 
                    ? product.price * (1 - product.discount_percent / 100)
                    : product.price;
                
                return (
                  <div 
                    key={product.product_id}
                    onClick={() => toggleProduct(product)}
                    className={`flex items-center gap-4 p-3 rounded-2xl cursor-pointer transition-all ${
                      isSelected 
                        ? 'bg-emerald-50 border-2 border-emerald-500' 
                        : 'bg-stone-50 border-2 border-transparent hover:border-stone-200'
                    }`}
                  >
                    {/* Image */}
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-white flex-shrink-0">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl bg-stone-100">🍽️</div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-stone-900 truncate">{product.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-bold text-emerald-700">${finalPrice?.toFixed(2)}</span>
                        {product.promotion_active && finalPrice < product.price && (
                          <span className="text-sm text-stone-400 line-through">${product.price?.toFixed(2)}</span>
                        )}
                      </div>
                      {product.promotion_active && product.promotion_label && (
                        <Badge className="mt-1 bg-orange-100 text-orange-700 border-0 text-xs">
                          {product.promotion_label}
                        </Badge>
                      )}
                    </div>

                    {/* Add Button */}
                    <button 
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isSelected 
                          ? 'bg-emerald-600 text-white' 
                          : 'bg-white border border-stone-200 text-stone-600 hover:border-emerald-500 hover:text-emerald-600'
                      }`}
                    >
                      <Plus className={`w-5 h-5 ${isSelected ? 'rotate-45' : ''} transition-transform`} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-stone-100 bg-stone-50">
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1 rounded-full"
              onClick={onProceed}
            >
              No gracias
            </Button>
            <Button 
              className="flex-1 bg-emerald-700 hover:bg-emerald-800 rounded-full"
              onClick={handleAddAndProceed}
              data-testid="upsell-proceed-btn"
            >
              {selectedProducts.length > 0 
                ? `Agregar ${selectedProducts.length} y pagar` 
                : 'Continuar al pago'
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
